/* eslint-disable max-lines */
import { FrameAnimationProps } from 'common-package';
import { Decimal } from 'decimal.js';
import { storagePrefixFixedV2 } from '@/constant';

export const controItemExtranDom: Partial<
  Record<
    Massage.ControlItemOptions,
    {
      label: string;
      iconName?: string;
      prefix?: React.ReactNode;
      suffix?: React.ReactNode;
      formatText?: (text: string) => string;
    }
  >
> = {
  'shockwave.power': {
    label: '强度',
    suffix: 'mJ',
  },
  'shockwave.frequency': {
    label: '频率',
    suffix: 'Hz',
  },
  'radio_frequency.power': {
    label: '能量',
    suffix: '%',
  },
  'robot.force': {
    label: '力度',
    suffix: 'N',
  },
  'robot.position': {
    label: '位置',
  },
  'robot.step': {
    label: '步骤',
  },
  'robot.velocity': {
    label: '速度',
    suffix: '档',
  },
  'robot.voice': {
    label: '音量',
  },
  'massage.tech': {
    label: '手法',
  },
  'robot.height': {
    label: '距离人体表面高度',
    suffix: 'cm',
    formatText: text => text && new Decimal(text || 0)?.div(10).toString(),
  },
  'moxibustion.tech': {
    label: '灸法',
  },
  'radio_frequency.duration': {
    label: '时长',
    suffix: '分钟',
  },
  'shockwave.basic_hits': {
    label: '单条走线打击次数',
    suffix: '次',
  },
  'shockwave.nodule_hits': {
    label: '单个痛点打击次数',
    suffix: '次',
  },
  'massage.intensify_treatment': {
    label: '局部加强',
  },
  'hot_stone.duration': {
    label: '时长',
    suffix: '分钟',
  },
  'hot_stone.power': {
    label: '强度',
    suffix: '%',
  },
  'hot_stone.temperature_level': {
    label: '温度',
    suffix: '档',
  },
  'hot_stone.mode': {
    label: '模式',
  },
};

// 根据模型solution映射功效label位置
export const SOLUTION_TO_LABEL_COORDS: Partial<
  Record<
    Massage.ModelSolution,
    {
      top: string;
      right: string;
      arrowCoords?: {
        top: string;
      };
    }
  >
> = {
  'massage-yzc': {
    top: '54%',
    right: '61%',
  },
  'massage-yzc-10-points': {
    top: '54%',
    right: '61%',
  },
  'massage-shoulder-lv': {
    top: '40%',
    right: '61%',
  },
  'massage-shoulder-lv-v2': {
    top: '30%',
    right: '58%',
  },
  'massage-waist-lv': {
    top: '66%',
    right: '61%',
    arrowCoords: {
      top: '70%',
    },
  },
  'massage-yzc-zhai': {
    top: '55%',
    right: '61%',
  },
  shoulder: {
    top: '46%',
    right: '64%',
  },
  'shock-shoulder-yst-1': {
    top: '35%',
    right: '65.7%',
  },
  'shock-waist-yst-1': {
    top: '63%',
    right: '63%',
  },
  'shock-waistback-yst-1': {
    top: '55%',
    right: '63%',
  },
  'shock-waistback-yst-2': {
    top: '55%',
    right: '63%',
  },
  back: {
    top: '62%',
    right: '64%',
  },
  'shock-shoulder-teacher-zhai': {
    top: '29%',
    right: '66%',
  },
  'shock-waist-teacher-zhai': {
    top: '63%',
    right: '65%',
  },
  'shock-shoulder-deji': {
    top: '36%',
    right: '61%',
  },
  'shock-waist-deji': {
    top: '68%',
    right: '61%',
  },
  'shoulder-rf-lc-1': {
    top: '36%',
    right: '63%',
  },
  'shoulder-rf-lc-2': {
    top: '37%',
    right: '63%',
  },
  'waist-rf-lc-1': {
    top: '67%',
    right: '63%',
  },
  'abdomen-rf-lc-1': {
    top: '70%',
    right: '63%',
  },
  'abdomen-rf-lc-clockwise-1': {
    top: '70%',
    right: '63%',
  },
  'shoulder-rf-sc-1': {
    top: '38%',
    right: '63%',
  },
  'waist-rf-sc-1': {
    top: '67%',
    right: '63%',
  },
  'waist-rf-sc-2': {
    top: '67%',
    right: '63%',
  },
  'waist-rf-route-1': {
    top: '67%',
    right: '63%',
  },
  'waist-rf-route-2': {
    top: '67%',
    right: '63%',
  },
  'shoulder-rf-route-1': {
    top: '37%',
    right: '63%',
  },
  'back-rf-route-1': {
    top: '39%',
    right: '63%',
  },
};

// 自定义模型solution贴图，不使用konva绘制
export const CUSTOM_SOLUTION_BG: Partial<
  Record<Massage.ModelSolution, { imageUrl: string; width: string; left: string; top: string }>
> = {
  shoulder: {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/adaddqwf.png',
    width: '200px',
    left: '33%',
    top: '26%',
  },
  'shock-shoulder-yst-1': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/aaaaasqfqwfqw-min.png',
    width: '210px',
    left: '31.5%',
    top: '23%',
  },
  'shock-waist-yst-1': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/yestadqdq-qdw.png',
    width: '220px',
    left: '30%',
    top: '58%',
  },
  'shock-waistback-yst-1': {
    imageUrl: storagePrefixFixedV2 + '/image/aqooasdasffqw-min.png',
    width: '217px',
    left: '30.9%',
    top: '23%',
  },
  'shock-waistback-yst-2': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/shock-waistback-yst-2-bg.png',
    width: '217px',
    left: '30.9%',
    top: '23%',
  },
  back: {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/waist-bg-asqwewq.png',
    width: '157px',
    left: '35.6%',
    top: '51%',
  },
  'shock-shoulder-teacher-zhai': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/shoulder-zhai-qasdqwd-min.png',
    width: '200px',
    left: '32.2%',
    top: '31.5%',
  },
  'shock-waist-teacher-zhai': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/sw-waist-zhai-qwdqwfa.png',
    width: '170px',
    left: '35.1%',
    top: '49%',
  },
  'shock-shoulder-deji': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/deji-sw-shoulder-min.png',
    width: '130px',
    left: '38.5%',
    top: '25%',
  },
  'shock-waist-deji': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/asdasasdasf-min.png',
    width: '115px',
    left: '40.5%',
    top: '60%',
  },
  'shoulder-rf-lc-1': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/shoulder-rf-lc-1.png',
    width: '227px',
    left: '30.5%',
    top: '25%',
  },
  'shoulder-rf-lc-2': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/rf/Mask group-min.png',
    width: '225px',
    left: '30.3%',
    top: '25.5%',
  },
  'waist-rf-lc-1': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/asdqwfwqfqw-min.png',
    width: '235px',
    left: '29.2%',
    top: '56%',
  },
  'abdomen-rf-lc-1': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/rf/aadaq.png',
    width: '200px',
    left: '32%',
    top: '65%',
  },
  'abdomen-rf-lc-clockwise-1': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/rf/aadaq.png',
    width: '200px',
    left: '32%',
    top: '65%',
  },
  'shoulder-rf-sc-1': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/rf/caaq.png',
    width: '225px',
    left: '31%',
    top: '24.3%',
  },
  'waist-rf-sc-1': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/qwfwqasdasf-min.png',
    width: '234px',
    left: '29%',
    top: '55%',
  },
  'waist-rf-sc-2': {
    imageUrl: storagePrefixFixedV2 + '/image/effect-intro/asdq1qwdqw-min.png',
    width: '238px',
    left: '29%',
    top: '58%',
  },
  'waist-rf-route-1': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/rf/eqwasdasf.png',
    width: '211px',
    left: '31.2%',
    top: '61%',
  },
  'waist-rf-route-2': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/rf/qsqfdq.png',
    width: '211px',
    left: '31.2%',
    top: '61%',
  },
  'shoulder-rf-route-1': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/rf/adaqfq.png',
    width: '229px',
    left: '30%',
    top: '24.5%',
  },
  'back-rf-route-1': {
    imageUrl: storagePrefixFixedV2 + '/model-texture/rf/asdadqf.png',
    width: '212px',
    left: '31.5%',
    top: '24.5%',
  },
};

/**
 * note: 之前的射频方案不包括手法，新加的包含射频的手法与之前的手法映射，以在按摩控制页面渲染solution
 */
export const RF_TECH_SOLUTION_TO_STATIC_SOLUTION: Partial<Record<Massage.ModelSolution, Massage.ModelSolution>> = {
  'shoulder-rf-lc-1': 'shoulder-RF',
  'shoulder-rf-lc-2': 'shoulder-RF',
  'shoulder-rf-sc-1': 'shoulder-RF',
  'shoulder-rf-route-1': 'shoulder-RF',
  'waist-rf-lc-1': 'waist-RF',
  'waist-rf-sc-1': 'waist-RF',
  'waist-rf-sc-2': 'waist-RF',
  'waist-rf-route-1': 'waist-RF',
  'waist-rf-route-2': 'waist-RF',
  'abdomen-rf-lc-1': 'abdomen-RF',
  'abdomen-rf-lc-clockwise-1': 'abdomen-RF',
  'back-rf-route-1': 'back-RF',
};

/** 射频各方案走线动效配置 */
export const RF_PLAN_FRAME_ANIMATION_CONFIG: Partial<Record<Massage.ModelSolution, Partial<FrameAnimationProps>>> = {
  'shoulder-rf-lc-1': {
    url: storagePrefixFixedV2 + '/image/animation/shoulder-lc-2-min.png',
    picDirection: 'row',
    count: 195,
    aspectRatio: 1.074,
    duration: 30,
    width: 230,
  },
  'shoulder-rf-lc-2': {
    url: storagePrefixFixedV2 + '/image/animation/shoulder-lc-2-min.png',
    picDirection: 'row',
    count: 195,
    aspectRatio: 1.074,
    duration: 30,
    width: 230,
  },
  'shoulder-rf-sc-1': {
    url: storagePrefixFixedV2 + '/image/animation/shoulder-sc.png',
    picDirection: 'row',
    count: 420,
    aspectRatio: 1.01,
    duration: 60,
    width: 210,
  },
  'shoulder-rf-route-1': {
    url: storagePrefixFixedV2 + '/image/animation/shoulder-rf-route-v2-min.png',
    count: 420,
    picDirection: 'row',
    aspectRatio: 0.7,
    duration: 60,
    width: 135,
  },
  'waist-rf-lc-1': {
    url: storagePrefixFixedV2 + '/image/animation/waist-lc-min.png',
    picDirection: 'row',
    count: 424,
    aspectRatio: 1.192,
    duration: 30,
    width: 200,
  },
  'waist-rf-sc-1': {
    url: storagePrefixFixedV2 + '/image/animation/waist-sc.png',
    picDirection: 'row',
    count: 416,
    aspectRatio: 0.908,
    duration: 60,
    width: 180,
  },
  'waist-rf-sc-2': {
    url: storagePrefixFixedV2 + '/image/animation/waist-sc.png',
    picDirection: 'row',
    count: 416,
    aspectRatio: 0.908,
    duration: 60,
    width: 180,
  },
  'waist-rf-route-1': {
    url: storagePrefixFixedV2 + '/image/animation/wasit-route-min.png',
    picDirection: 'row',
    count: 416,
    aspectRatio: 0.817,
    duration: 60,
    width: 160,
  },
  'waist-rf-route-2': {
    url: storagePrefixFixedV2 + '/image/animation/wasit-route-min.png',
    picDirection: 'row',
    count: 416,
    aspectRatio: 0.817,
    duration: 60,
    width: 160,
  },
  'abdomen-rf-lc-1': {
    width: 170,
    url: storagePrefixFixedV2 + '/image/animation/abdomen-lc-min.png',
    picDirection: 'row',
    count: 424,
    aspectRatio: 1.139,
    duration: 30,
  },
  'abdomen-rf-lc-clockwise-1': {
    width: 170,
    url: storagePrefixFixedV2 + '/image/animation/abdomen-rf-lc-invert-1-min.png',
    picDirection: 'row',
    count: 424,
    aspectRatio: 1.139,
    duration: 30,
  },
  'back-rf-route-1': {
    url: storagePrefixFixedV2 + '/image/animation/back-route.png',
    picDirection: 'row',
    count: 419,
    aspectRatio: 0.408,
    duration: 60,
    width: 130,
  },
};
